import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface LearnedRecipeSummary {
  learnedId: string;
  userId: string;
  recipeId: string;
  learnedAt: string;
}

interface UseLearnedRecipesOptions {
  userId?: string;
  admin?: boolean;
}

interface UseLearnedRecipesReturn {
  learnedRecipes: LearnedRecipeSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useLearnedRecipes(options: UseLearnedRecipesOptions = {}): UseLearnedRecipesReturn {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.crafting.learnedRecipes(options.userId),
    queryFn: async () => {
      const path =
        options.admin && options.userId
          ? `/arches/crafting/admin/learned/${options.userId}`
          : '/arches/crafting/learned';
      const { data } = await api.get<{
        learnedRecipes: LearnedRecipeSummary[];
        total: number;
      }>(path);
      return data;
    },
  });

  return {
    learnedRecipes: data?.learnedRecipes ?? [],
    total: data?.total ?? 0,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load learned recipes' : null,
    refresh: () => {
      refetch();
    },
  };
}
